<template>
  <div class="panel-fragment">
    <div class="panel-content is-full-sized">
      <!--
        Layout: the panel-header keeps the H1 + NEW MAP affordance only.
        Filters and chips live in a dedicated .forge-controls block
        below so they don't compete with the H1 for flex space (which
        was scrunching the count + wrapping the NEW MAP label).
      -->
      <div class="panel-header">
        <h1>
          <strong>{{ totalMaps }}</strong> {{ $t('page.create.maps.header_unit') }}
        </h1>

        <router-link
          to="/create/map/new"
          class="default-button">
          {{ $t('page.create.maps.new') }}
        </router-link>
      </div>

      <div class="forge-controls">
        <div class="forge-toolbar">
          <input
            type="text"
            class="forge-search"
            :placeholder="$t('page.create.common.search_placeholder')"
            :aria-label="$t('page.create.common.search_placeholder')"
            v-model="filters.name"
            @input="onFilterInput" />

          <select
            class="forge-size-filter"
            v-model="filters.size"
            :aria-label="$t('a11y_portal.filter_size')"
            @change="onFilterChange">
            <option value="">{{ $t('page.create.common.size_any') }}</option>
            <option
              v-for="size in sizeChoices"
              :key="size"
              :value="size">
              {{ $t(`map.size.${size}.label`) }}
            </option>
          </select>

          <select
            class="forge-size-filter"
            v-model="filters.sort"
            :aria-label="$t('a11y_portal.sort_by')"
            @change="onFilterChange">
            <option
              v-for="opt in sortOptions"
              :key="opt"
              :value="opt">
              {{ $t(`page.create.common.sort.${opt}`) }}
            </option>
          </select>
        </div>

        <div class="forge-chips">
          <button
            v-for="chip in chipChoices"
            :key="chip"
            class="forge-chip"
            :class="{ 'is-active': activeChip === chip }"
            :aria-pressed="activeChip === chip ? 'true' : 'false'"
            @click="setChip(chip)">
            {{ $t(`page.create.common.chip.${chip}`) }}
          </button>
        </div>
      </div>

      <v-scrollbar
        v-if="loaded"
        class="content">
        <div
          v-if="maps.length === 0"
          class="full-sized-text">
          {{ $t('page.create.maps.no_results') }}
        </div>
        <template v-else>
          <table class="default-table maps-table forge-cards">
            <tr
              v-for="map in maps"
              :key="map.id">
              <td class="forge-card-thumb">
                <router-link
                  :to="`/create/map/view/${map.share_token}`"
                  tabindex="-1"
                  aria-hidden="true">
                  <img
                    v-if="map.thumbnail"
                    :src="map.thumbnail"
                    :alt="map.game_metadata.name" />
                  <div
                    v-else
                    class="forge-card-thumb-placeholder">
                    <svgicon name="galaxy" aria-hidden="true" />
                  </div>
                </router-link>
              </td>
              <td class="forge-card-body">
                <h2>
                  <router-link :to="`/create/map/view/${map.share_token}`">
                    {{ map.game_metadata.name }}
                  </router-link>
                </h2>
                <em>
                  {{ $t(`map.size.${map.game_metadata.size}.toast`) }}
                  <span
                    class="toast"
                    v-if="!map.author && map.is_official">
                    {{ $t('page.create.maps.official') }}
                  </span>
                  <span
                    class="toast"
                    v-else-if="map.author">
                    {{ $t('page.create.common.by') }} {{ map.author.name }}
                  </span>
                  <span
                    class="toast"
                    v-if="!map.published_at">
                    {{ $t('page.create.common.draft') }}
                  </span>
                </em>
                <div class="reactions row-reactions">
                  <button
                    class="reaction-button"
                    v-tooltip="$t('page.create.common.like')"
                    :aria-label="`${$t('page.create.common.like')}: ${map.likes || 0}`"
                    @click="react(map, 'likes')">
                    <svgicon name="check" aria-hidden="true" />{{ map.likes || 0 }}
                  </button>
                  <button
                    class="reaction-button"
                    v-tooltip="$t('page.create.common.dislike')"
                    :aria-label="`${$t('page.create.common.dislike')}: ${map.dislikes || 0}`"
                    @click="react(map, 'dislikes')">
                    <svgicon name="close" aria-hidden="true" />{{ map.dislikes || 0 }}
                  </button>
                  <button
                    class="reaction-button"
                    v-tooltip="$t('page.create.common.favorite')"
                    :aria-label="`${$t('page.create.common.favorite')}: ${map.favorites || 0}`"
                    @click="react(map, 'favorites')">
                    <svgicon name="bookmark" aria-hidden="true" />{{ map.favorites || 0 }}
                  </button>
                </div>
              </td>
              <td class="actions">
                <div class="forge-card-actions">
                  <router-link
                    class="default-button squared"
                    v-tooltip="$t('page.create.common.view')"
                    :aria-label="$t('page.create.common.view')"
                    :to="`/create/map/view/${map.share_token}`">
                    <svgicon name="eye" aria-hidden="true" />
                  </router-link>
                  <router-link
                    class="default-button squared"
                    v-tooltip="$t('page.create.maps.edit')"
                    :aria-label="$t('page.create.maps.edit')"
                    :to="`/create/map/${map.id}`">
                    <svgicon name="pencil" aria-hidden="true" />
                  </router-link>
                  <router-link
                    class="default-button"
                    :to="`/create/scenario/new/${map.share_token}`">
                    {{ $t('page.create.maps.use_for_scenario') }}
                  </router-link>
                  <button
                    class="default-button squared"
                    v-tooltip="$t('page.create.common.share_tooltip')"
                    :aria-label="$t('page.create.common.share')"
                    @click="share(map)">
                    <svgicon name="share" aria-hidden="true" />
                  </button>
                </div>
              </td>
            </tr>
          </table>

          <div
            v-if="totalPages > 1"
            class="forge-pagination">
            <button
              class="default-button"
              :disabled="page <= 1"
              @click="goToPage(page - 1)">
              <svgicon name="caret-left" aria-hidden="true" /> {{ $t('page.create.common.previous') }}
            </button>
            <span class="forge-pagination-info">
              {{ $t('page.create.common.page_of', { current: page, total: totalPages }) }}
            </span>
            <button
              class="default-button"
              :disabled="page >= totalPages"
              @click="goToPage(page + 1)">
              {{ $t('page.create.common.next') }} <svgicon name="caret-right" aria-hidden="true" />
            </button>
          </div>
        </template>
      </v-scrollbar>
      <loading-mask v-else />
    </div>

    <v-scrollbar class="panel-aside">
      <div class="panel-aside-info">
        <h2>{{ $t('page.create.maps.about_heading') }}</h2>
        <p v-html="$t('page.create.maps.about_body')"></p>
      </div>
      <div class="panel-aside-info">
        <h2>{{ $t('page.create.maps.roadmap_heading') }}</h2>
        <p v-html="$t('page.create.maps.roadmap_body')"></p>
      </div>
      <hr class="margin">
    </v-scrollbar>
  </div>
</template>

<script>
import config from '@/config';
import { copyToClipboard } from '@/utils/clipboard';

import Loading from '@/portal/mixins/Loading';
import LoadingMask from '@/portal/components/LoadingMask.vue';

export default {
  name: 'create-maps',
  mixins: [Loading],
  data() {
    return {
      maps: [],
      totalMaps: 0,
      totalPages: 1,
      page: 1,
      // Mirrors the wizard's choices in Map.vue:582. Kept literal here so
      // the dropdown doesn't depend on having a Map open first.
      sizeChoices: [80, 120, 200, 360, 500, 750],
      sortOptions: ['newest', 'most_liked', 'most_favorited', 'most_played'],
      chipChoices: ['all', 'officials', 'mine', 'favorited', 'drafts'],
      activeChip: 'all',
      filters: {
        name: '',
        size: '',
        sort: 'newest',
      },
      // Debounced reloader, created in `created()` so each component
      // instance gets its own cancelable timer.
      debouncedReload: null,
    };
  },
  methods: {
    async loadData() {
      // Build params, dropping any blank values so the backend doesn't
      // try to filter on an empty string (game_metadata->>'name' like '%')
      // would still match every row, but `size: ""` would crash
      // String.to_integer/1 on the server.
      const params = { page: this.page, sort: this.filters.sort };
      if (this.filters.name) params.name = this.filters.name;
      if (this.filters.size) params.size = this.filters.size;
      // Chip filter — only one is active at a time. 'all' sends nothing.
      if (this.activeChip !== 'all') params[this.activeChip] = 'true';

      const resp = await this.releaseLoading(this.$axios.get('/maps', { params }));
      this.maps = resp.data;
      this.totalMaps = parseInt(resp.headers.total, 10) || 0;
      this.totalPages = parseInt(resp.headers['total-pages'], 10) || 1;
    },
    setChip(chip) {
      // Toggle off if you click the active chip — Vue chip components
      // usually behave this way, and "All" is the off-state anyway.
      this.activeChip = this.activeChip === chip ? 'all' : chip;
      this.page = 1;
      this.loadData();
    },
    onFilterInput() {
      // Reset to page 1 on every change — staying on page 5 of a filter
      // that only has 2 pages of results just shows an empty list.
      this.page = 1;
      this.debouncedReload();
    },
    onFilterChange() {
      this.page = 1;
      this.loadData();
    },
    goToPage(target) {
      if (target < 1 || target > this.totalPages) return;
      this.page = target;
      this.loadData();
    },
    async react(map, kind) {
      try {
        await this.$axios.post(`/maps/${map.id}/folders/${kind}`);
        // Optimistic UI — bump the count locally so the user sees
        // immediate feedback. Backend is the source of truth on reload.
        this.$set(map, kind, (map[kind] || 0) + 1);
      } catch (err) {
        this.$toastError(this.$t('page.create.common.error_generic'));
      }
    },
    async share(map) {
      // The /forge URL (not the SPA route) so the link unfurls with a
      // title + thumbnail when pasted on Discord etc.
      const copied = await copyToClipboard(`${config.BASE_URL}/forge/map/${map.share_token}`);
      if (copied) {
        this.$toasted.success(this.$t('page.create.common.share_copied'));
      } else {
        this.$toastError(this.$t('page.create.common.error_generic'));
      }
    },
  },
  created() {
    this.debouncedReload = this._.debounce(this.loadData, 300);
  },
  mounted() {
    this.loadData();
  },
  components: {
    LoadingMask,
  },
};
</script>
